import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import useAuthStore from "../store/useAuthStore";
import { toast } from "react-toastify";
import { ReactComponent as GalleryIcon } from "../assets/gallery-send-svgrepo-com.svg";
import { ReactComponent as CameraIcon } from "../assets/photo-camera-photocamera-svgrepo-com.svg";
import { addTag, getTags } from "../services/TagServices";
import { saveCard, scanCard } from "../services/uploadServices";
import { getAvailableProfiles } from "../services/ProfileServices";

const loadImage = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // Some mobile formats need the image-element fallback.
    }
  }

  const objectUrl = URL.createObjectURL(file);
  const image = document.createElement("img");

  try {
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = objectUrl;
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const optimizeImage = async (
  file,
  { maxWidth = 1280, maxHeight = 960, quality = 0.8 } = {}
) => {
  const startedAt = performance.now();
  const image = await loadImage(file);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const scale = Math.min(1, maxWidth / sourceWidth, maxHeight / sourceHeight);
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Unable to optimize the selected image");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, width, height);
  image.close?.();

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) =>
        result
          ? resolve(result)
          : reject(new Error("Unable to optimize the selected image")),
      "image/jpeg",
      quality
    );
  });
  const optimizedFile = new File(
    [blob],
    `${file.name.replace(/\.[^.]+$/, "")}.jpg`,
    {
      type: "image/jpeg",
      lastModified: Date.now(),
    }
  );

  console.info("Card image optimization", {
    originalBytes: file.size,
    optimizedBytes: optimizedFile.size,
    reductionPercent:
      file.size > 0
        ? Math.round((1 - optimizedFile.size / file.size) * 100)
        : 0,
    durationMs: Math.round(performance.now() - startedAt),
    dimensions: `${width}x${height}`,
  });

  return optimizedFile;
};

const CheckIcon = ({ className = "h-4 w-4" }) => (
  <svg
    aria-hidden="true"
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m5 12 4 4L19 6" />
  </svg>
);

const SparkleIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m12 3 1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2L12 3Z" />
    <path d="m18.5 14 .8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z" />
    <path d="m5 13 .8 2.2L8 16l-2.2.8L5 19l-.8-2.2L2 16l2.2-.8L5 13Z" />
  </svg>
);

const WorkflowSteps = ({ hasImage, hasCard }) => {
  const steps = ["Add photos", "Review details", "Save card"];
  const currentStep = hasCard ? 2 : 1;

  return (
    <ol className="mt-6 flex items-start" aria-label="Card scanning progress">
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const complete = stepNumber < currentStep || (hasImage && index === 0 && hasCard);
        const active = stepNumber === currentStep;

        return (
          <React.Fragment key={step}>
            <li className="flex min-w-0 flex-col items-center text-center">
              <span
                className={`grid h-8 w-8 place-items-center rounded-full text-xs font-bold transition ${
                  complete
                    ? "bg-emerald-500 text-white"
                    : active
                      ? "bg-indigo-600 text-white ring-4 ring-indigo-100"
                      : "bg-slate-100 text-slate-400"
                }`}
              >
                {complete ? <CheckIcon /> : stepNumber}
              </span>
              <span
                className={`mt-2 max-w-[6rem] text-[11px] font-semibold leading-4 sm:max-w-none sm:text-xs ${
                  active || complete ? "text-slate-700" : "text-slate-400"
                }`}
              >
                {step}
              </span>
            </li>
            {index < steps.length - 1 && (
              <span
                aria-hidden="true"
                className={`mt-4 h-0.5 flex-1 ${
                  complete ? "bg-emerald-400" : "bg-slate-200"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
};

const ImagePickerCard = ({
  id,
  title,
  description,
  preview,
  required,
  busy,
  onChange,
  onRemove,
}) => (
  <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
    <div className="flex items-start justify-between gap-3 px-4 pb-3 pt-4 sm:px-5">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
      </div>
      <span
        className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
          required
            ? "bg-indigo-50 text-indigo-700"
            : "bg-slate-100 text-slate-500"
        }`}
      >
        {required ? "Required" : "Optional"}
      </span>
    </div>

    {preview ? (
      <div className="px-3 pb-3 sm:px-4 sm:pb-4">
        <div className="relative aspect-[1.58/1] overflow-hidden rounded-xl bg-slate-950">
          <img
            src={preview}
            alt={`${title} preview`}
            className="h-full w-full object-contain"
          />
          <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-2.5 py-1 text-[11px] font-bold text-white shadow-lg">
            <CheckIcon className="h-3.5 w-3.5" /> Ready
          </span>
          {busy && (
            <div className="absolute inset-0 grid place-items-center bg-slate-950/65 text-white">
              <ClipLoader size={24} color="#ffffff" />
            </div>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <label
            htmlFor={`${id}-replace`}
            className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-700 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
          >
            <GalleryIcon className="h-4 w-4" /> Replace
            <input
              id={`${id}-replace`}
              type="file"
              accept="image/*"
              className="sr-only"
              disabled={busy}
              onChange={onChange}
            />
          </label>
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            className="min-h-11 rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-600 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
          >
            Remove
          </button>
        </div>
      </div>
    ) : (
      <div className="mx-3 mb-3 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-4 sm:mx-4 sm:mb-4 sm:p-5">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-400/20">
          {busy ? <ClipLoader size={20} color="#4f46e5" /> : <CameraIcon className="h-6 w-6" />}
        </div>
        <p className="mt-3 text-center text-xs leading-5 text-slate-500">
          Keep the card flat, well-lit, and inside the frame.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-2 min-[390px]:grid-cols-2">
          <label
            htmlFor={`${id}-camera`}
            className="inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
          >
            <CameraIcon className="h-5 w-5" /> Take photo
            <input
              id={`${id}-camera`}
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              disabled={busy}
              onChange={onChange}
            />
          </label>
          <label
            htmlFor={`${id}-gallery`}
            className="inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-indigo-300 hover:text-indigo-700"
          >
            <GalleryIcon className="h-5 w-5" /> Choose image
            <input
              id={`${id}-gallery`}
              type="file"
              accept="image/*"
              className="sr-only"
              disabled={busy}
              onChange={onChange}
            />
          </label>
        </div>
      </div>
    )}
  </section>
);

const Field = ({ label, name, value, onChange, type = "text", placeholder }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold text-slate-600">
      {label}
    </span>
    <input
      className="min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-[15px] text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
    />
  </label>
);

const PhotoUploadPage = () => {
  const token = useAuthStore((state) => state.token);
  const navigate = useNavigate();
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [userFile, setUserFile] = useState(null);
  const [userPreview, setUserPreview] = useState(null);
  const [imageProcessing, setImageProcessing] = useState("");
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [visitingCard, setVisitingCard] = useState(null);
  const [profileList, setProfileList] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [tagsList, setTagsList] = useState([]);

  useEffect(() => {
    return () => {
      if (frontPreview) URL.revokeObjectURL(frontPreview);
    };
  }, [frontPreview]);

  useEffect(() => {
    return () => {
      if (backPreview) URL.revokeObjectURL(backPreview);
    };
  }, [backPreview]);

  useEffect(() => {
    return () => {
      if (userPreview) URL.revokeObjectURL(userPreview);
    };
  }, [userPreview]);

  const allProfiles = async () => {
    const res = await getAvailableProfiles();
    const profiles = (res?.data?.profiles || []).filter(
      (profile) =>
        profile.isSelectable !== false &&
        (!profile.status || profile.status === "PUBLISHED")
    );
    setProfileList(profiles);
    setSelectedProfileId(res?.data?.workspaceCampaignId || "");
  };

  const loadTags = async () => {
    const res = await getTags();
    setTagsList(res?.data || []);
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    Promise.all([allProfiles(), loadTags()]).catch((error) => {
      console.error("Error loading upload options:", error);
      toast.error("Some card options could not be loaded");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const processImage = async (event, type) => {
    const input = event.target;
    const selectedFile = input.files?.[0];
    if (!selectedFile) return;

    setImageProcessing(type);
    try {
      const optimizedFile = await optimizeImage(selectedFile);
      const previewUrl = URL.createObjectURL(optimizedFile);

      if (type === "front") {
        setFrontFile(optimizedFile);
        setFrontPreview(previewUrl);
        setVisitingCard(null);
      } else if (type === "back") {
        setBackFile(optimizedFile);
        setBackPreview(previewUrl);
        setVisitingCard(null);
      } else {
        setUserFile(optimizedFile);
        setUserPreview(previewUrl);
      }
    } catch (error) {
      console.error("Error preparing image:", error);
      toast.error("This image could not be prepared. Please choose another one.");
    } finally {
      setImageProcessing("");
      input.value = "";
    }
  };

  const resetImages = () => {
    setFrontFile(null);
    setBackFile(null);
    setUserFile(null);
    setFrontPreview(null);
    setBackPreview(null);
    setUserPreview(null);
    setImageProcessing("");
  };

  const handleRemoveFront = () => {
    resetImages();
    setVisitingCard(null);
  };

  const handleRemoveBack = () => {
    setBackFile(null);
    setBackPreview(null);
    setVisitingCard(null);
  };

  const handlePhotoUpload = async () => {
    if (!frontFile) {
      toast.error("Add the front of the card before scanning");
      return;
    }

    const formData = new FormData();
    formData.append("front", frontFile);
    if (backFile) formData.append("back", backFile);

    try {
      setLoading(true);
      const scanStartedAt = performance.now();
      const res = await scanCard(formData);
      const data = res.data;

      if (res.status === 200) {
        console.info("Card scan timing", {
          clientTotalMs: Math.round(performance.now() - scanStartedAt),
          azureScanMs: data.timing?.scanTimeMs,
        });
        setVisitingCard(data.visitingCard);
        toast.success("Scan complete. Review the details before saving.");
        requestAnimationFrame(() => {
          document
            .getElementById("card-details")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } else {
        toast.error(data.message || "Card scan failed");
      }
    } catch (error) {
      console.error("Error during card scan:", error);
      toast.error(error.response?.data?.message || "Card scan failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setVisitingCard((currentCard) => ({ ...currentCard, [name]: value }));
  };

  const handleAddTag = async () => {
    const enteredName = window.prompt("Enter a new tag name:");
    const name = enteredName?.trim();
    if (!name) return;

    try {
      await addTag(name);
      await loadTags();
      setVisitingCard((currentCard) => ({ ...currentCard, tag: name }));
      toast.success("Tag added");
    } catch (error) {
      toast.error(error.response?.data?.message || "Tag could not be added");
    }
  };

  const handleSave = async () => {
    if (!visitingCard || !frontFile) return;

    try {
      setUpdating(true);
      const saveStartedAt = performance.now();
      const formData = new FormData();
      formData.append("profileId", selectedProfileId);
      formData.append("company", visitingCard.company || "");
      formData.append("position", visitingCard.position || "");
      formData.append("firstName", visitingCard.firstName || "");
      formData.append("lastName", visitingCard.lastName || "");
      formData.append("mobile", visitingCard.mobile || "");
      formData.append("email", visitingCard.email || "");
      formData.append("website", visitingCard.website || "");
      formData.append("remark", visitingCard.remark || "");
      formData.append("tag", visitingCard.tag || "");
      formData.append("metadata", visitingCard.metadata || "{}");
      formData.append("front", frontFile);
      if (backFile) formData.append("back", backFile);
      if (userFile) formData.append("userImage", userFile);

      const res = await saveCard(formData);
      const data = res.data;

      if (res.status === 201 || res.status === 200) {
        console.info("Card save timing", {
          clientTotalMs: Math.round(performance.now() - saveStartedAt),
          ...data.timing,
          queuedJobs: data.automationJobs?.map((job) => job.type) || [],
        });
        toast.success("Card saved. Automations are processing.");
        setVisitingCard(null);
        resetImages();
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(data.message || "Card could not be saved");
      }
    } catch (error) {
      console.error("Error saving visiting card:", error);
      toast.error(error.response?.data?.message || "Card could not be saved. Try again.");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setVisitingCard(null);
    resetImages();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="w-full px-4 py-5 sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <motion.header
          className="relative overflow-hidden rounded-3xl border border-indigo-100 bg-gradient-to-br from-white via-indigo-50/70 to-violet-100/70 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/70 sm:px-8 sm:py-8"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-indigo-300/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-white shadow-sm">
              <SparkleIcon /> Smart card scanner
            </span>
            <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
              Turn a business card into a contact
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600 sm:text-base">
              Take a clear photo, verify the extracted details, then save when
              everything looks right.
            </p>
          </div>
          <div className="relative max-w-xl">
            <WorkflowSteps hasImage={Boolean(frontPreview)} hasCard={Boolean(visitingCard)} />
          </div>
        </motion.header>

        <motion.section
          className="mt-5 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:mt-6 sm:p-6"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.06 }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-indigo-600">
                Step 1
              </span>
              <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-950">
                Add card photos
              </h2>
              <p className="mt-1 text-sm leading-6 text-slate-500">
                Front is required. Add the back when it contains useful details.
              </p>
            </div>
            {frontPreview && (
              <button
                type="button"
                onClick={handleRemoveFront}
                className="self-start rounded-xl px-3 py-2 text-sm font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              >
                Clear photos
              </button>
            )}
          </div>

          <div
            className={`mt-5 grid gap-4 ${
              frontPreview
                ? "lg:grid-cols-2"
                : "lg:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]"
            }`}
          >
            <ImagePickerCard
              id="front-card"
              title="Front of card"
              description="Capture the name and primary contact information."
              preview={frontPreview}
              required
              busy={imageProcessing === "front"}
              onChange={(event) => processImage(event, "front")}
              onRemove={handleRemoveFront}
            />

            {frontPreview && (
              <ImagePickerCard
                id="back-card"
                title="Back of card"
                description="Add it when there are more phone numbers, links, or addresses."
                preview={backPreview}
                busy={imageProcessing === "back"}
                onChange={(event) => processImage(event, "back")}
                onRemove={handleRemoveBack}
              />
            )}

            {!frontPreview && (
              <aside className="hidden rounded-2xl bg-gradient-to-br from-slate-950 to-slate-900 p-5 text-white lg:block">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-indigo-300">
                  For the best scan
                </p>
                <h3 className="mt-2 text-lg font-bold">A clear photo saves review time</h3>
                <ul className="mt-5 space-y-4 text-sm text-slate-300">
                  {[
                    "Place the card on a plain, contrasting surface.",
                    "Avoid glare, shadows, and cropped corners.",
                    "Hold your phone directly above the card.",
                  ].map((tip) => (
                    <li key={tip} className="flex gap-3">
                      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-400/15 text-emerald-300">
                        <CheckIcon className="h-3 w-3" />
                      </span>
                      <span className="leading-5">{tip}</span>
                    </li>
                  ))}
                </ul>
              </aside>
            )}
          </div>

          {frontPreview && (
            <div className="mt-5 rounded-2xl bg-slate-950 p-3 sm:flex sm:items-center sm:justify-between sm:gap-5 sm:p-4">
              <div className="flex items-start gap-3 px-1 py-1 text-white">
                <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/10 text-indigo-200">
                  <svg
                    aria-hidden="true"
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 3 4 6v5c0 5.2 3.4 8.6 8 10 4.6-1.4 8-4.8 8-10V6l-8-3Z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </span>
                <div>
                  <p className="text-sm font-semibold">
                    Nothing is saved during scanning
                  </p>
                  <p className="mt-0.5 text-xs leading-5 text-slate-400">
                    Images are stored only after you review and save the card.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handlePhotoUpload}
                disabled={loading || Boolean(imageProcessing)}
                className="mt-3 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-indigo-500 px-6 text-sm font-bold text-white shadow-lg shadow-indigo-950/30 transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 sm:mt-0 sm:w-auto"
              >
                {loading ? (
                  <>
                    <ClipLoader size={18} color="#ffffff" /> Scanning card…
                  </>
                ) : (
                  <>
                    <SparkleIcon /> {visitingCard ? "Scan again" : "Scan card"}
                  </>
                )}
              </button>
            </div>
          )}
        </motion.section>

        {visitingCard && (
          <motion.section
            id="card-details"
            className="scroll-mt-32 mt-5 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm sm:mt-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="border-b border-slate-100 px-4 py-5 sm:px-6 sm:py-6">
              <div className="flex items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-100 text-emerald-700">
                  <CheckIcon className="h-5 w-5" />
                </span>
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">
                    Step 2 · Scan complete
                  </span>
                  <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-950">
                    Review contact details
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Correct anything the scanner missed before saving.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="First name" name="firstName" value={visitingCard.firstName} onChange={handleFieldChange} placeholder="First name" />
                <Field label="Last name" name="lastName" value={visitingCard.lastName} onChange={handleFieldChange} placeholder="Last name" />
                <Field label="Company" name="company" value={visitingCard.company} onChange={handleFieldChange} placeholder="Company name" />
                <Field label="Job title" name="position" value={visitingCard.position} onChange={handleFieldChange} placeholder="Designation or role" />
                <Field label="Mobile" name="mobile" type="tel" value={visitingCard.mobile} onChange={handleFieldChange} placeholder="Include country code" />
                <Field label="Email" name="email" type="email" value={visitingCard.email} onChange={handleFieldChange} placeholder="name@company.com" />
                <div className="sm:col-span-2">
                  <Field label="Website" name="website" type="url" value={visitingCard.website} onChange={handleFieldChange} placeholder="https://company.com" />
                </div>
              </div>

              <div className="my-6 h-px bg-slate-100" />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-600">Campaign</span>
                  <select
                    className="min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-[15px] text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                    value={selectedProfileId}
                    onChange={(event) => setSelectedProfileId(event.target.value)}
                  >
                    <option value="">No campaign automation</option>
                    {profileList.some((profile) => profile.visibility === "SHARED") && (
                      <optgroup label="Workspace campaigns">
                        {profileList
                          .filter((profile) => profile.visibility === "SHARED")
                          .map((profile) => (
                            <option key={profile.id} value={profile.id}>
                              {profile.name}
                            </option>
                          ))}
                      </optgroup>
                    )}
                    {profileList.some((profile) => profile.visibility === "PRIVATE") && (
                      <optgroup label="My campaigns">
                        {profileList
                          .filter((profile) => profile.visibility === "PRIVATE")
                          .map((profile) => (
                            <option key={profile.id} value={profile.id}>
                              {profile.name}
                            </option>
                          ))}
                      </optgroup>
                    )}
                  </select>
                  <span className="mt-1.5 block text-[11px] leading-4 text-slate-400">
                    Uses this campaign for the card you are saving. Change your default in Profiles.
                  </span>
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-600">Tag</span>
                  <span className="flex gap-2">
                    <select
                      name="tag"
                      value={visitingCard.tag || ""}
                      onChange={handleFieldChange}
                      className="min-h-12 min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3.5 text-[15px] text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                    >
                      <option value="">No tag</option>
                      {tagsList.map((tag) => (
                        <option key={tag.id || tag.name} value={tag.name}>{tag.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="min-h-12 shrink-0 rounded-xl border border-indigo-200 bg-indigo-50 px-3.5 text-sm font-bold text-indigo-700 transition hover:bg-indigo-100"
                    >
                      + Add
                    </button>
                  </span>
                </label>

                <label className="block sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-600">Remark</span>
                  <textarea
                    className="min-h-24 w-full resize-y rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-[15px] text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                    name="remark"
                    value={visitingCard.remark || ""}
                    onChange={handleFieldChange}
                    placeholder="Add a note about this contact"
                  />
                </label>
              </div>

              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Visitor photo</h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Optional. Add a face photo to recognize the contact later.
                    </p>
                  </div>
                  <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-500 ring-1 ring-slate-200">Optional</span>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-[7rem_1fr] sm:items-center">
                  <div className="relative mx-auto h-28 w-28 overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200 sm:mx-0">
                    {userPreview ? (
                      <img src={userPreview} alt="Visitor preview" className="h-full w-full object-cover" />
                    ) : (
                      <div className="grid h-full w-full place-items-center text-slate-300">
                        <svg aria-hidden="true" className="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                          <circle cx="12" cy="8" r="4" />
                          <path d="M4 21a8 8 0 0 1 16 0" />
                        </svg>
                      </div>
                    )}
                    {imageProcessing === "visitor" && (
                      <div className="absolute inset-0 grid place-items-center bg-white/80 dark:bg-slate-950/80">
                        <ClipLoader size={22} color="#4f46e5" />
                      </div>
                    )}
                  </div>

                  <div className="grid gap-2 min-[390px]:grid-cols-2">
                    <label htmlFor="visitor-camera" className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-800">
                      <CameraIcon className="h-4 w-4" /> Take photo
                      <input id="visitor-camera" type="file" accept="image/*" capture="environment" className="sr-only" onChange={(event) => processImage(event, "visitor")} />
                    </label>
                    <label htmlFor="visitor-gallery" className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-indigo-300 hover:text-indigo-700">
                      <GalleryIcon className="h-4 w-4" /> Choose image
                      <input id="visitor-gallery" type="file" accept="image/*" className="sr-only" onChange={(event) => processImage(event, "visitor")} />
                    </label>
                    {userPreview && (
                      <button type="button" onClick={() => { setUserFile(null); setUserPreview(null); }} className="min-h-10 text-sm font-semibold text-rose-600 min-[390px]:col-span-2">
                        Remove visitor photo
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mobile-safe-bottom sticky bottom-0 z-20 border-t border-slate-200 bg-white/95 p-4 shadow-[0_-12px_30px_-24px_rgba(15,23,42,0.7)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:static sm:flex sm:justify-end sm:gap-3 sm:px-6 sm:py-5 sm:shadow-none">
              <button type="button" onClick={handleCancel} disabled={updating} className="mb-2 min-h-12 w-full rounded-xl border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 sm:mb-0 sm:w-auto">
                Cancel
              </button>
              <button type="button" onClick={handleSave} disabled={updating || Boolean(imageProcessing)} className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-7 text-sm font-bold text-white shadow-lg shadow-indigo-200 transition hover:from-indigo-700 hover:to-violet-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                {updating ? (
                  <><ClipLoader size={18} color="#ffffff" /> Saving card…</>
                ) : (
                  <><CheckIcon className="h-4 w-4" /> Save card</>
                )}
              </button>
            </div>
          </motion.section>
        )}
      </div>
    </main>
  );
};

export default PhotoUploadPage;
